import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { createGate } from 'effector-react';
import { signout } from '@features/common';
import type { FormInstance } from '@unicorn/effector-form';
import { getComplexes, getBuildings, getApartments, getProperties } from './api';
import type {
  Complex,
  Building,
  Apartment,
  Property,
  ObjectType,
  GetComplexesResponse,
  GetBuildingsResponse,
  GetApartmentsResponse,
  GetPropertiesResponse,
  ComplexTree,
  BuildingTree,
  ObjectsTreeFormAdapterParams,
  ObjectsTreeSetValuePayload,
  ObjectsTreeValue,
} from './types';

export const complexKey = 'complex';
export const buildingKey = 'building';
const apartmentKey = 'apartment';
const propertyKey = 'property';

const propertyIdPattern = /^property-(\d+)-\d+$/;

const getPropertyId = (value: string | number) => {
  const match = String(value).match(propertyIdPattern);

  return match ? Number(match[1]) : null;
};

const toFormValue = (value: (string | number)[], objectTypes: ObjectType[]) => {
  const hasApartments = objectTypes.includes(apartmentKey);
  const hasProperties = objectTypes.includes(propertyKey);
  const apartments = value.filter((id): id is number => typeof id === 'number');

  const properties = Array.from(
    new Set(value.map(getPropertyId).filter((id): id is number => id !== null))
  );

  if (hasApartments && hasProperties) {
    return { apartments, properties };
  }

  return hasProperties ? properties : apartments;
};

const toTreeValue = (
  value: ObjectsTreeValue,
  objectTypes: ObjectType[],
  properties: Property[]
) => {
  const hasApartments = objectTypes.includes(apartmentKey);
  const hasProperties = objectTypes.includes(propertyKey);

  if (!hasApartments || !hasProperties || Array.isArray(value)) {
    if (!hasProperties || !Array.isArray(value)) {
      return Array.isArray(value) ? value : [];
    }

    const selectedProperties = new Set(value.map(Number));

    return properties
      .filter(({ id }) => {
        const propertyId = getPropertyId(id);

        return propertyId !== null && selectedProperties.has(propertyId);
      })
      .map(({ id }) => id);
  }

  const selectedProperties = new Set(value.properties);

  return [
    ...value.apartments,
    ...properties
      .filter(({ id }) => {
        const propertyId = getPropertyId(id);

        return propertyId !== null && selectedProperties.has(propertyId);
      })
      .map(({ id }) => id),
  ];
};

export const ObjectsTreeControlGate = createGate<{
  objectTypes: ObjectType[];
}>({
  defaultState: {
    objectTypes: [],
  },
});

export const $complexes = createStore<Complex[]>([]);
export const $buildings = createStore<Building[]>([]);
export const $apartments = createStore<Apartment[]>([]);
export const $properties = createStore<Property[]>([]);

export const $isComplexesLoading = createStore(false);
export const $isBuildingsLoading = createStore(false);
export const $isApartmentsLoading = createStore(false);
export const $isPropertiesLoading = createStore(false);

export const createObjectsTreeFormAdapter = <T>({
  form,
  name,
  objectTypes,
}: ObjectsTreeFormAdapterParams<T>) => {
  const setValue = createEvent<ObjectsTreeSetValuePayload>();

  const $values = combine(form.$values, $properties, (formValues, properties) => {
    const values = formValues as Record<string, unknown>;
    const value = (values[name] ?? []) as ObjectsTreeValue;

    return {
      ...formValues,
      [name]: toTreeValue(value, objectTypes, properties),
    };
  });

  sample({
    clock: setValue,
    fn: ({ value }) => ({
      path: name,
      value: toFormValue(value, objectTypes),
    }),
    target: form.setValue,
  });

  return {
    ...form,
    $values,
    values: $values,
    setValue,
  } as FormInstance<T>;
};

export const $isLoading = combine(
  $isComplexesLoading,
  $isBuildingsLoading,
  $isApartmentsLoading,
  $isPropertiesLoading,
  (isComplexesLoading, isBuildingsLoading, isApartmentsLoading, isPropertiesLoading) =>
    isComplexesLoading || isBuildingsLoading || isApartmentsLoading || isPropertiesLoading
);

export const $objects = combine(
  $complexes,
  $buildings,
  $apartments,
  $properties,
  ObjectsTreeControlGate.state,
  (
    complexes,
    buildings,
    apartments,
    properties,
    { objectTypes }
  ): ComplexTree[] | BuildingTree[] | (Apartment | Property)[] => {
    const hasComplexes = objectTypes.includes(complexKey);
    const hasBuildings = objectTypes.includes(buildingKey);
    const hasApartments = objectTypes.includes(apartmentKey);
    const hasProperties = objectTypes.includes(propertyKey);

    const isComplexLeaf = hasComplexes && !hasBuildings;
    const isBuildingLeaf = hasBuildings && !hasApartments && !hasProperties;

    const getNodeId = (
      type: typeof complexKey | typeof buildingKey,
      id: number | string,
      isLeaf: boolean
    ) => (isLeaf ? id : `${type}-${id}`);

    const apartmentsByBuildingId = apartments.reduce<Record<string, Apartment[]>>(
      (acc, apartment) => {
        const parentBuildingId = String(apartment.parentId);

        if (!acc[parentBuildingId]) {
          acc[parentBuildingId] = [];
        }

        acc[parentBuildingId].push(apartment);

        return acc;
      },
      {}
    );

    const propertiesByBuildingId = properties.reduce<Record<string, Property[]>>(
      (acc, property) => {
        const parentBuildingId = String(property.parentId);

        if (!acc[parentBuildingId]) {
          acc[parentBuildingId] = [];
        }

        acc[parentBuildingId].push(property);

        return acc;
      },
      {}
    );

    const getBuildingChildren = (buildingId: number | string) => [
      ...(hasApartments ? (apartmentsByBuildingId[String(buildingId)] ?? []) : []),
      ...(hasProperties ? (propertiesByBuildingId[String(buildingId)] ?? []) : []),
    ];

    const buildingsByComplexId = buildings.reduce<Record<string, BuildingTree[]>>(
      (acc, building) => {
        const parentComplexId = String(building.parentId);
        const children = isBuildingLeaf ? undefined : getBuildingChildren(building.id);

        const hasChildren = Boolean(children?.length);

        if (!isBuildingLeaf && !hasChildren) {
          return acc;
        }

        if (!acc[parentComplexId]) {
          acc[parentComplexId] = [];
        }

        acc[parentComplexId].push({
          ...building,
          id: getNodeId(buildingKey, building.id, isBuildingLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      },
      {}
    );

    if (hasComplexes) {
      return complexes.reduce<ComplexTree[]>((acc, complex) => {
        const children = hasBuildings
          ? (buildingsByComplexId[String(complex.id)] ?? [])
          : undefined;

        const hasChildren = Boolean(children?.length);

        if (!isComplexLeaf && !hasChildren) {
          return acc;
        }

        acc.push({
          ...complex,
          id: getNodeId(complexKey, complex.id, isComplexLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      }, []);
    }

    if (hasBuildings) {
      return buildings.reduce<BuildingTree[]>((acc, building) => {
        const children = isBuildingLeaf ? undefined : getBuildingChildren(building.id);

        const hasChildren = Boolean(children?.length);

        if (!isBuildingLeaf && !hasChildren) {
          return acc;
        }

        acc.push({
          ...building,
          id: getNodeId(buildingKey, building.id, isBuildingLeaf),
          ...(children?.length ? { children } : {}),
        });

        return acc;
      }, []);
    }

    return [...(hasApartments ? apartments : []), ...(hasProperties ? properties : [])];
  }
);

export const fxGetComplexes = createEffect<void, GetComplexesResponse, Error>().use(
  getComplexes
);

export const fxGetBuildings = createEffect<void, GetBuildingsResponse, Error>().use(
  getBuildings
);

export const fxGetApartments = createEffect<void, GetApartmentsResponse, Error>().use(
  getApartments
);

export const fxGetProperties = createEffect<void, GetPropertiesResponse, Error>().use(
  getProperties
);

/** Init */
/** Запрос ЖК */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(complexKey),
  target: fxGetComplexes,
});

/** Запрос зданий */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(buildingKey),
  target: fxGetBuildings,
});

/** Запрос квартир */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(apartmentKey),
  target: fxGetApartments,
});

/** Запрос помещений */
sample({
  clock: ObjectsTreeControlGate.open,
  filter: ({ objectTypes }) => objectTypes.includes(propertyKey),
  target: fxGetProperties,
});

$complexes
  .on(fxGetComplexes.doneData, (_, result) =>
    Array.isArray(result?.items)
      ? result.items.map((item) => ({
          id: item?.id,
          label: item?.title,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isComplexesLoading
  .on(fxGetComplexes.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);

$buildings
  .on(fxGetBuildings.doneData, (_, result) =>
    Array.isArray(result?.buildings)
      ? result.buildings.map((item) => ({
          id: item?.id,
          label: item?.title,
          parentId: item?.complex?.id,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isBuildingsLoading
  .on(fxGetBuildings.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);

$apartments
  .on(fxGetApartments.doneData, (_, result) =>
    Array.isArray(result?.items)
      ? result.items.map((item) => ({
          id: item?.apartment?.id,
          label: item?.apartment?.title,
          parentId: item?.building?.id,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isApartmentsLoading
  .on(fxGetApartments.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);

$properties
  .on(fxGetProperties.doneData, (_, result) =>
    Array.isArray(result?.items)
      ? result.items.map((item) => ({
          id: `${propertyKey}-${item.property.id}-${item.building.id}`,
          label: item.property.title,
          parentId: item.building.id,
        }))
      : []
  )
  .reset([signout, ObjectsTreeControlGate.close]);

$isPropertiesLoading
  .on(fxGetProperties.pending, (_, pending) => pending)
  .reset([signout, ObjectsTreeControlGate.close]);
